import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  Avatar,
} from '@mui/material';
import { ContentCut, Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, rgba(233, 30, 99, 0.9) 0%, rgba(173, 20, 87, 0.9) 100%),
          url('https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: 'rgba(255,255,255,0.2)',
                margin: '0 auto 16px',
              }}
            >
              <ContentCut sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Bella Vista Salon
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Professional Beauty & Wellness Management System
            </Typography>
          </Box>

          {/* Login Form */}
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
              <Lock sx={{ mr: 1, color: '#E91E63' }} />
              <Typography variant="h5" fontWeight={500} color="#2C3E50">
                Sign In to Your Account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 4 }}
                variant="outlined"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #AD1457 0%, #880E4F 100%)',
                  },
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <Box mt={4} textAlign="center">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Don't have an account?{' '}
                <Button
                  variant="text"
                  onClick={() => window.location.href = '/signup'}
                  sx={{
                    color: '#E91E63',
                    textTransform: 'none',
                    fontWeight: 600,
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Sign Up Here
                </Button>
              </Typography>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Admin:</strong> admin@bellavista.com / password123
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Client:</strong> client1@example.com / password123
                </Typography>
              </Box>
            </Box>
          </CardContent>

          {/* Footer */}
          <Box
            sx={{
              backgroundColor: '#F8F9FA',
              p: 2,
              textAlign: 'center',
              borderTop: '1px solid #E9ECEF',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              © 2024 Bella Vista Salon. Secure Beauty Management Platform
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
